'use client';

import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import BlazeSlider from 'blaze-slider';
import 'blaze-slider/dist/blaze.css';
import Image from 'next/image';
import ArrowButton from './ArrowButton';
import VimeoOverlayPlayer from './VimeoOverlayPlayer';
import { PlaySVG } from './Icons';
import useDisableScroll from './use-disable-scroll';

const getBlazeConfig = (videos) => ({
  all: {
    transitionDuration: 600,
    slideGap: '16px',
    loop: true,
    slidesToShow: 1
  },
  '(min-width: 640px)': {
    slidesToShow: 3
  },
  '(min-width: 1024px)': {
    slidesToShow: videos.length < 4 ? videos.length : 4,
    slideGap: '32px'
  }
});

export default function VideoCarouselSlider({
  heading,
  subheading,
  videos = []
}) {
  const carouselElementRef = useRef(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    if (!sliderRef.current && carouselElementRef.current && videos.length) {
      sliderRef.current = new BlazeSlider(
        carouselElementRef.current,
        getBlazeConfig(videos)
      );
    } else if (sliderRef.current && carouselElementRef.current) {
      sliderRef.current.refresh();
    }

    return () => {
      if (sliderRef.current) {
        sliderRef.current.destroy();
        sliderRef.current = null;
      }
    };
  }, [videos, carouselElementRef, sliderRef]);

  const [modalVideo, setModalVideo] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = (video) => {
    setModalVideo(video);
    setIsModalOpen(true);
  };

  const hideModal = (e) => {
    if (e.target !== e.currentTarget) return;
    setIsModalOpen(false);
  };

  useDisableScroll(isModalOpen);

  // Reset modal to support history change
  useLayoutEffect(() => {
    return () => {
      setIsModalOpen(false);
      setModalVideo(undefined);
    };
  }, []);

  return (
    <>
      <div
        className={`blaze-slider ${heading || subheading ? 'mt-10 sm:mt-15 lg:mt-20' : ''}`}
        ref={carouselElementRef}
      >
        <div className="blaze-container mx-auto max-w-272">
          <ArrowButton direction="prev" videosLength={videos.length} />
          <div className="blaze-track-container mx-auto">
            <ul className={`blaze-track`}>
              {videos.map((item, index) => (
                <VideoSlide key={index} video={item} showModal={showModal} />
              ))}
            </ul>
            <ArrowButton direction="next" videosLength={videos.length} />
          </div>
          <div className="blaze-pagination indicators-dark mt-10 flex"></div>
        </div>
      </div>

      {/* The z-index here is higher than the chat widget because the widget blocks the product links */}
      <div
        className={`${isModalOpen ? 'flex' : 'hidden'} fixed top-0 right-0 bottom-0 left-0 z-9999999 flex-row flex-wrap items-center justify-center bg-black/30`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => hideModal(e)}
      >
        {modalVideo && isModalOpen ? (
          <VimeoOverlayPlayer video={modalVideo} hideModal={hideModal} />
        ) : (
          <div className="body-r absolute bg-black p-3 text-white">
            Video is currently unavailable.
          </div>
        )}
      </div>
    </>
  );
}

const VideoSlide = ({ video, showModal }) => {
  const thumbnail = video?.video_thumbnail;

  return (
    <li
      className={`group relative cursor-pointer`}
      onClick={() => showModal(video)}
    >
      {thumbnail?.url && (
        <div className="relative mx-auto aspect-342/607 w-full max-w-62 sm:max-w-87.5">
          <Image
            src={thumbnail.url}
            fill
            sizes="(min-width: 1536px) 342px, (min-width: 1024px) 274px, (min-width: 768px) 192px, 192px"
            className="bg-modal-light-grey object-cover object-center"
            alt={
              thumbnail?.description ||
              thumbnail?.title?.replace(/\.[^/.]+$/, '')
            }
          />
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <button className="group pointer-events-auto" aria-label="play video">
          <PlaySVG />
        </button>
      </div>
    </li>
  );
};
